import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Users, Calendar, Trophy } from 'lucide-react';
import { Team } from '@/types/database';
import { TeamMembersSection } from './TeamMembersSection';
import { TeamPhotoGallery } from './TeamPhotoGallery';
import { TeamVideoGallery } from './TeamVideoGallery';
import { TeamFollowButton } from './TeamFollowButton';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';

interface PublicTeamViewProps {
  team: Team;
  currentUserId?: string;
}

export const PublicTeamView = ({ team, currentUserId }: PublicTeamViewProps) => {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  
  const currentUser = supabaseUser || internalUser;
  const isOwner = currentUserId === team.created_by;
  const userType = internalUser ? 'internal' : 'supabase';

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          {team.team_description && (
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">About</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300">{team.team_description}</p>
              </CardContent>
            </Card>
          )}

          {/* Team Stats/Info */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Team Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-sports-green" />
                  <span className="text-gray-300">Sport: {team.sport_type || 'Not specified'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-sports-green" />
                  <span className="text-gray-300">Age Group: {team.age_group || 'Open'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-sports-green" />
                  <span className="text-gray-300">Division: {team.division || 'General'}</span>
                </div>
                {team.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-sports-green" />
                    <span className="text-gray-300">{team.location}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Follow Button - Show for logged-in non-owners */}
          {currentUser && !isOwner && (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="pt-6">
                <TeamFollowButton
                  teamId={team.team_id}
                  userId={currentUser.id}
                  userType={userType}
                  size="default"
                  showCount={true}
                  className="justify-center w-full"
                />
              </CardContent>
            </Card>
          )}

          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Quick Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Created</span>
                <span className="text-white">
                  {team.created_at ? new Date(team.created_at).toLocaleDateString() : 'Unknown'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Sport</span>
                <span className="text-white">{team.sport_type || 'Not specified'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Location</span>
                <span className="text-white">{team.city || team.location || 'Not specified'}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Public Team Members Section - Read-only */}
      <TeamMembersSection 
        teamId={team.team_id}
        teamName={team.team_name}
        teamSport={team.sport_type}
        currentUserId={currentUserId}
        canManage={false} // Public view is read-only
      />


      {/* Public Team Photo Gallery - Read-only */}
      <TeamPhotoGallery 
        teamId={team.team_id}
        canManage={false} // Public view is read-only
      />

      {/* Team Video Gallery - Read only for public view */}
      <TeamVideoGallery 
        teamId={team.team_id}
        canManage={false}
      />
    </div>
  );
};